import { useEffect, useRef, useState } from "react";

// Feature-detect ONCE during initial render so the cursor elements exist
// before the animation effect runs (refs must be populated).
const detectEnabled = () => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return fine && !reduce;
};

// Custom terminal-themed cursor: a green ring that trails a crisp dot.
// Grows on interactive elements. Disabled on touch / reduced-motion.
export const TerminalCursor = () => {
  const ringRef = useRef(null);
  const dotRef = useRef(null);
  const [enabled] = useState(detectEnabled);

  useEffect(() => {
    if (!enabled) return;

    const ring = ringRef.current;
    const dot = dotRef.current;
    if (!ring || !dot) return;

    document.body.classList.add("lx-custom-cursor");

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;
    let ringX = mouseX;
    let ringY = mouseY;
    let visible = false;

    const show = () => {
      if (visible) return;
      visible = true;
      ring.style.opacity = "1";
      dot.style.opacity = "1";
    };

    const onMove = (e) => {
      show();
      mouseX = e.clientX;
      mouseY = e.clientY;
      dot.style.transform = `translate(${mouseX}px, ${mouseY}px)`;
    };

    const isInteractive = (el) =>
      el &&
      el.closest &&
      el.closest(
        "a, button, input, textarea, select, [role='button'], .lx-navlink, [data-cursor='interactive']"
      );

    const onOver = (e) => {
      ring.classList.toggle("is-active", !!isInteractive(e.target));
    };

    const onDown = () => (ring.style.opacity = "0.6");
    const onUp = () => (ring.style.opacity = "1");
    const onLeave = () => {
      ring.style.opacity = "0";
      dot.style.opacity = "0";
      visible = false;
    };

    let raf;
    const render = () => {
      raf = requestAnimationFrame(render);
      ringX += (mouseX - ringX) * 0.2;
      ringY += (mouseY - ringY) * 0.2;
      ring.style.transform = `translate(${ringX}px, ${ringY}px)`;
    };
    // Position immediately at center so it's not stuck at 0,0.
    dot.style.transform = `translate(${mouseX}px, ${mouseY}px)`;
    ring.style.transform = `translate(${ringX}px, ${ringY}px)`;
    raf = requestAnimationFrame(render);

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseover", onOver);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    document.addEventListener("mouseleave", onLeave);

    return () => {
      cancelAnimationFrame(raf);
      document.body.classList.remove("lx-custom-cursor");
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onOver);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      <div
        ref={ringRef}
        data-testid="terminal-cursor-ring"
        className="lx-cursor-ring"
        style={{ opacity: 0 }}
      />
      <div
        ref={dotRef}
        data-testid="terminal-cursor-dot"
        className="lx-cursor-dot"
        style={{ opacity: 0 }}
      />
    </>
  );
};
